import React, { useEffect, useMemo, useRef, useState } from "react";
import "./puzzle.css";
import jigsawImage from "../../img/jigsaw-image.jpg";
import { useLocation, useNavigate } from "react-router-dom";
import { addDoc, collection, doc, getDoc, limit, onSnapshot, orderBy, query, runTransaction, setDoc } from "firebase/firestore";
import { database } from "../../database/firebase";
const HOLD_TTL_MS = 5000;
const DRAG_PAYLOAD_TYPE = "application/x-jigsaw-payload";
const SWAP_RETRY_LIMIT = 3;
const VOTING_COOLDOWN_SEC = 2 * 60;

function isContentionError(error) {
  const msg = String(error?.message || error || "").toLowerCase();
  return (
    msg.includes("does not match the required base version") ||
    msg.includes("aborted due to cross-transaction contention") ||
    msg.includes("too much contention")
  );
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function formatMoveError(error) {
  const raw = String(error?.message || error || "").trim();
  if (!raw) return "Move failed for an unknown reason.";
  if (raw.toLowerCase().includes("does not match the required base version")) {
    return `Move failed: firestore version conflict. ${raw}`;
  }
  return `Move failed: ${raw}`;
}

function formatDurationFromSeconds(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

function getPlayerIdentityKeys(player) {
  return [
    player?.userId,
    player?.userid,
    player?.id,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

function stripNullWords(value) {
  return String(value ?? "")
    .replace(/\bnull\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function resolvePlayerName(player, fallbackOrder) {
  const fromName = stripNullWords(player?.name);
  if (fromName) return fromName;
  const first = stripNullWords(player?.firstName ?? player?.first_name);
  const last = stripNullWords(player?.lastName ?? player?.last_name);
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const email = String(player?.email ?? "").trim();
  if (email) return email;
  const empId = String(player?.emp_id ?? player?.employeeId ?? "").trim();
  if (empId) return empId;
  return `Player ${fallbackOrder || 1}`;
}

function getPlayerNameById(players, playerId) {
  const id = String(playerId || "").trim();
  if (!id) return "";
  const idx = (players || []).findIndex((p) => getPlayerIdentityKeys(p).includes(id));
  if (idx < 0) return "";
  return resolvePlayerName(players[idx], idx + 1);
}

export default function Puzzle() {
  const [isVotingPopupOpen, setIsVotingPopupOpen] = useState(false);
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const userData = useMemo(() => {
    try {
      const raw =
        localStorage.getItem("userData") ||
        sessionStorage.getItem("userData") ||
        "{}";
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }, []);
  const userId = String(userData?.userId ?? userData?.userid ?? userData?.id ?? "").trim();
  const sessionId = String(userData?.sessionId ?? "").trim();
  const lobbyName = String(userData?.lobby ?? location.state?.lobbyName ?? "").trim();

  const puzzleStateRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/state/puzzle`);
  }, [lobbyName, sessionId]);
  const puzzleLocksRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/puzzleLocks`);
  }, [lobbyName, sessionId]);
  const puzzleTilesRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/puzzleTiles`);
  }, [lobbyName, sessionId]);
  const lobbyDocRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return doc(database, `lobbies/${sessionId}/lobby/${lobbyName}`);
  }, [lobbyName, sessionId]);

  const [puzzleState, setPuzzleState] = useState(null);
  const [puzzleLockState, setPuzzleLockState] = useState(null);
  const [puzzleTilesState, setPuzzleTilesState] = useState([]);
  const [arePuzzleTilesLoaded, setArePuzzleTilesLoaded] = useState(false);
  const [players, setPlayers] = useState([]);
  const [puzzlePresenceByUserId, setPuzzlePresenceByUserId] = useState({});
  const [localPuzzleActivity, setLocalPuzzleActivity] = useState({
    activity: "idle",
    heldTileId: null,
    holdUntilMs: null,
    updatedAtMs: 0,
  });
  const [myRole, setMyRole] = useState("");
  const [myActivity, setMyActivity] = useState("idle");
  const [myHeldTileId, setMyHeldTileId] = useState(null);
  const [myHoldUntilMs, setMyHoldUntilMs] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [sendingChat, setSendingChat] = useState(false);
  const [lastSeenChatAtMs, setLastSeenChatAtMs] = useState(() => Date.now());
  const [selectedVoteTarget, setSelectedVoteTarget] = useState("");
  const [optimisticTiles, setOptimisticTiles] = useState(null);
  const [dropStatus, setDropStatus] = useState("");
  const [touchDrag, setTouchDrag] = useState(null);
  // Lobby-status mirrored into state so we can defer the gameover redirect
  // until the eliminated player has dismissed their kicked-out popup.
  const [lobbyStatus, setLobbyStatus] = useState("");
  const [showKickedPopup, setShowKickedPopup] = useState(false);
  const wasEliminatedRef = useRef(false);
  const dragFromPosRef = useRef(null);
  const dragTileIdRef = useRef(null);
  const touchDragRef = useRef(null);
  const dropInFlightRef = useRef(false);
  const pendingMoveRef = useRef(null);
  const setMyStatusRef = useRef(null);
  const lastAppliedVersionRef = useRef(0);
  const isDraggingRef = useRef(false);
  // True once HTML5 dragstart has fired for the current gesture. Some browsers
  // (notably Firefox) fire a synthetic mouseup on the source element shortly
  // after dragstart, which used to race with our status writes and flip the
  // player back to "idle" mid-drag. We use this flag to ignore that synthetic
  // mouseup while a real native drag is still in progress.
  const dragStartedRef = useRef(false);
  const latestDeferredTilesRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const tileSeedStartedRef = useRef(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const gridSize = Number(puzzleState?.gridSize || 0) || 0;
  const tiles =
    Array.isArray(puzzleTilesState) && puzzleTilesState.length
      ? puzzleTilesState
      : Array.isArray(puzzleState?.tiles)
      ? puzzleState.tiles
      : [];
  const renderedTiles = Array.isArray(optimisticTiles) ? optimisticTiles : tiles;
  const totalTiles = gridSize ? gridSize * gridSize : 0;
  const lockByPos = useMemo(() => {
    const raw = puzzleLockState;
    if (!raw || typeof raw !== "object") return {};
    const next = {};
    for (const [posKey, value] of Object.entries(raw)) {
      if (!value || typeof value !== "object") continue;
      const lockUntilMs = Number(value.lockUntilMs || 0);
      if (lockUntilMs <= nowMs) continue;
      next[String(posKey)] = {
        userId: String(value.userId || "").trim(),
        userName: String(value.userName || "").trim(),
        lockedTileId: value.lockedTileId === null || value.lockedTileId === undefined ? null : Number(value.lockedTileId),
        lockedAtMs: Number(value.lockedAtMs || 0),
        lockUntilMs,
      };
    }
    return next;
  }, [puzzleLockState, nowMs]);
  const imageSrc = puzzleState?.imageKey
    ? `${process.env.REACT_APP_S3_PATH}${puzzleState.imageKey}`
    : jigsawImage;
  const puzzlePresenceRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/puzzlePresence`);
  }, [lobbyName, sessionId]);
  const isTileLockedByOther = (pos) => {
    const lock = lockByPos[String(pos)];
    return Boolean(lock && String(lock?.userId || "").trim() !== userId);
  };
  // Reflects whether the local user is actively interacting with the
  // puzzle right now. We deliberately use dragStartedRef + touchDragRef
  // (which only flip true when a real drag actually starts) instead of
  // isDraggingRef, because isDraggingRef is set on onMouseDown and could
  // get "stuck" if the mouseup lands outside the original tile.
  const isLocalPuzzleInteractionActive = () =>
    dragStartedRef.current ||
    Boolean(touchDragRef.current) ||
    dropInFlightRef.current ||
    Boolean(pendingMoveRef.current) ||
    myActivity === "holding" ||
    myActivity === "dragging";
  const applyRemoteTilesIfIdle = (remote) => {
    if (!remote || !Array.isArray(remote.tiles)) return false;
    if (isLocalPuzzleInteractionActive()) {
      latestDeferredTilesRef.current = remote;
      return false;
    }
    latestDeferredTilesRef.current = null;
    setPuzzleTilesState(remote.tiles);
    setOptimisticTiles(null);
    return true;
  };
  const chatCollectionRef = useMemo(() => {
    if (!sessionId || !lobbyName) return null;
    return collection(
      database,
      `lobbies/${sessionId}/lobby/${lobbyName}/chat/room/messages`
    );
  }, [lobbyName, sessionId]);

  const solvedCount = useMemo(() => {
    if (!renderedTiles.length) return 0;
    let c = 0;
    for (let i = 0; i < renderedTiles.length; i++) if (renderedTiles[i] === i) c++;
    return c;
  }, [renderedTiles]);
  const progressPct = totalTiles ? Math.round((solvedCount / totalTiles) * 100) : 0;
  const isRestrictedMover = useMemo(() => {
    const r = String(myRole || "").trim().toLowerCase();
    return r === "imposter" || r === "jester";
  }, [myRole]);

  // Map of every identity key -> player color, sourced from each player's
  // user doc (color is assigned at game start in waiting.jsx). Keyed by
  // userId, userid and id so any identity field can look it up.
  const playerColorById = useMemo(() => {
    const map = {};
    (players || []).forEach((p) => {
      const color = String(p?.color || "").trim();
      if (!color) return;
      [p?.userId, p?.userid, p?.id]
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .forEach((key) => {
          map[key] = color;
        });
    });
    return map;
  }, [players]);
  const isCorrectlyPlaced = (tileId, pos) =>
    Number.isFinite(Number(tileId)) &&
    Number.isFinite(Number(pos)) &&
    Number(tileId) === Number(pos);
  const votingState = puzzleState?.voting && typeof puzzleState.voting === "object" ? puzzleState.voting : {};
  const votesByUserId =
    votingState?.votesByUserId && typeof votingState.votesByUserId === "object"
      ? votingState.votesByUserId
      : {};
  const result = votingState?.result && typeof votingState.result === "object" ? votingState.result : null;
  const livePlayers = useMemo(
    () =>
      (players || []).filter((p) => {
        const pid = String(p?.userId || p?.id || "").trim();
        if (!pid) return false;
        const status = String(p?.status || "live").trim().toLowerCase();
        return status !== "offline";
      }),
    [players]
  );
  const activePlayers = useMemo(
    () =>
      livePlayers.filter(
        (p) => String(p?.status || "live").trim().toLowerCase() !== "eliminated"
      ),
    [livePlayers]
  );

  useEffect(() => {
    const t = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(t);
  }, []);
  const remainingSec = useMemo(() => {
    const startedAtMs = Number(puzzleState?.startedAtMs || 0);
    const totalTimeSec = Number(puzzleState?.totalTimeSec || 0);
    if (!startedAtMs || !totalTimeSec) return 0;
    const elapsed = Math.floor((nowMs - startedAtMs) / 1000);
    return Math.max(0, totalTimeSec - elapsed);
  }, [nowMs, puzzleState?.startedAtMs, puzzleState?.totalTimeSec]);
  const totalTimeSec = Number(puzzleState?.totalTimeSec || 0);
  const remainingLabel = useMemo(() => {
    const mm = Math.floor(remainingSec / 60);
    const ss = remainingSec % 60;
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }, [remainingSec]);
  const elapsedSec = useMemo(() => {
    return Math.max(0, totalTimeSec - remainingSec);
  }, [totalTimeSec, remainingSec]);
  const votingStartTimeSec = Math.max(
    0,
    Math.min(totalTimeSec || 0, Number(puzzleState?.votingStartTimeSec ?? 8 * 60))
  );
  const votingUnlockAtElapsedSec = Math.max(0, totalTimeSec - votingStartTimeSec);
  const votingDurationSec = Math.max(1, Number(puzzleState?.votingDurationSec || 60));
  const myVote = String(votesByUserId?.[userId] || "").trim();
  const voteCount = Object.keys(votesByUserId || {}).length;
  const allVotesSubmitted = activePlayers.length > 0 && voteCount >= activePlayers.length;
  const finalOutcome =
    votingState?.finalOutcome && typeof votingState.finalOutcome === "object"
      ? votingState.finalOutcome
      : null;
  const votingStartedAtMs = Number(votingState?.startedAtMs || 0);
  const votingRoundDurationSec = Math.max(1, Number(votingState?.durationSec || votingDurationSec));
  const votingEndsAtMs = Number(votingState?.endsAtMs || 0) || votingStartedAtMs + votingRoundDurationSec * 1000;
  const votingRemainingSec = votingState?.isOpen
    ? Math.max(0, Math.ceil((votingEndsAtMs - nowMs) / 1000))
    : 0;
  const votingCooldownUntilMs = Number(votingState?.cooldownUntilMs || 0);
	  const votingCooldownRemainingSec = Math.max(0, Math.ceil((votingCooldownUntilMs - nowMs) / 1000));
	  const votingUnlockRemainingSec = Math.max(0, votingUnlockAtElapsedSec - elapsedSec);
	  const eliminatedUserId = String(result?.eliminatedUserId || "").trim();
	  const eliminatedName = getPlayerNameById(livePlayers, eliminatedUserId) || "A player";
	  const myPlayerRow = (players || []).find((p) => getPlayerIdentityKeys(p).includes(userId));
	  const isEliminatedPlayer =
	    (Boolean(eliminatedUserId) && eliminatedUserId === userId) ||
	    String(myPlayerRow?.status || "").trim().toLowerCase() === "eliminated";

	  // The role of the player who was just eliminated, used by the
	  // kicked-out popup so the local user can also see why they were
	  // removed (imposter / jester / regular player).
	  const kickedRole = useMemo(() => {
	    const fromMyRow = String(myPlayerRow?.role || "").trim().toLowerCase();
	    const fromVotingResult = String(result?.eliminatedRole || "").trim().toLowerCase();
	    return fromMyRow || fromVotingResult || String(myRole || "").trim().toLowerCase();
	  }, [myPlayerRow?.role, myRole, result?.eliminatedRole]);

	  // Reach the local player's character (c1..c5) for the avatar in the
	  // kicked-out popup, mirroring the avatar logic on the waiting screen.
	  const myCharacter = useMemo(() => {
	    const fromRow = String(myPlayerRow?.character || "").trim().toLowerCase();
	    const fromUser = String(userData?.character || "").trim().toLowerCase();
	    return fromRow || fromUser;
	  }, [myPlayerRow?.character, userData?.character]);
	  // Non-elimination round outcomes (tie / no majority / all skip /
	  // timeout) — per-player elimination messages are derived separately
	  // below from the players snapshot so all eliminated rounds remain
	  // visible, not just the latest one.
	  const voteResultNonEliminationMessage = !result || result?.eliminatedUserId
	    ? ""
	    : result.reason === "no_majority"
	    ? "No majority. Nobody was voted out."
	    : result.reason === "tie"
	    ? "Voting tied. Nobody was voted out."
	    : result.reason === "all_skip"
	    ? "Everyone skipped. Nobody was voted out."
	    : "Voting ended. Nobody was voted out.";
	  // One notice per eliminated player, oldest first, so the history of
	  // votes is preserved across rounds.
	  const eliminationNotices = useMemo(() => {
	    const eliminated = (players || [])
	      .filter((p) => String(p?.status || "").toLowerCase() === "eliminated")
	      .sort((a, b) => Number(a?.eliminatedAtMs || 0) - Number(b?.eliminatedAtMs || 0));
	    return eliminated.map((p, idx) => {
	      const name = resolvePlayerName(p, idx + 1);
	      const role = String(p?.role || "").trim().toLowerCase();
	      const reason = String(p?.eliminatedReason || "").trim().toLowerCase();
	      if (role === "imposter" || reason === "imposter_voted_out") {
	        return `${name} was voted out. They were the imposter.`;
	      }
	      if (role === "jester" || reason === "jester_win") {
	        return `${name} was voted out. They were the jester and win the game.`;
	      }
	      return `${name} was voted out. but they were not the imposter.`;
	    });
	  }, [players]);
	  const finalOutcomeMessage = !finalOutcome
	    ? ""
	    : finalOutcome.reason === "jester_win"
	    ? "The jester was voted out and wins the game."
	    : finalOutcome.reason === "imposter_timeout_win"
	    ? "Time ran out. The imposter wins."
	    : finalOutcome.reason === "imposter_two_normals_out_win"
	    ? "Two players have been voted out. The imposter wins."
	    : finalOutcome.reason === "others_puzzle_completed"
	    ? "Puzzle completed. The imposter loses and the others win."
	    : "Game over.";
	  const isVotingForcedOpen = Boolean(votingState?.isOpen && !result);
	  const canStartVoting =
	    elapsedSec >= votingUnlockAtElapsedSec &&
	    !votingState?.isOpen &&
	    votingCooldownRemainingSec <= 0 &&
	    !finalOutcome &&
	    !isEliminatedPlayer &&
	    activePlayers.length > 1;
  const votingButtonStatus = votingState?.isOpen
    ? `Vote ${formatDurationFromSeconds(votingRemainingSec)}`
    : votingUnlockRemainingSec > 0
    ? `Unlocks in ${formatDurationFromSeconds(votingUnlockRemainingSec)}`
    : votingCooldownRemainingSec > 0
    ? `Cooldown ${formatDurationFromSeconds(votingCooldownRemainingSec)}`
    : "Ready";
  const perUserVoteCount = useMemo(() => {
    if (!allVotesSubmitted) return {};
    const tally = {};
    for (const target of Object.values(votesByUserId || {})) {
      const t = String(target || "").trim();
      if (!t || t === "skip") continue;
      tally[t] = (tally[t] || 0) + 1;
    }
    return tally;
	  }, [allVotesSubmitted, votesByUserId]);

  useEffect(() => {
    if (!puzzleStateRef) return undefined;
    const unsub = onSnapshot(
      puzzleStateRef,
      (snap) => {
        if (!snap.exists()) return;
        const next = snap.data() || {};
        const incomingVersion = Number(next?.version || 0);
        if (incomingVersion < lastAppliedVersionRef.current) return;
        lastAppliedVersionRef.current = incomingVersion;
        setPuzzleState(next);
      },
      (err) => {
        console.error("Puzzle state snapshot failed:", err);
      }
    );
    return () => unsub();
  }, [puzzleStateRef]);

  useEffect(() => {
    if (!puzzleTilesRef) return undefined;
    const unsub = onSnapshot(
      query(puzzleTilesRef, orderBy("pos", "asc")),
      (snap) => {
        const fallbackTiles = Array.isArray(puzzleState?.tiles) ? puzzleState.tiles : [];
        const expectedLength =
          gridSize > 0 ? gridSize * gridSize : fallbackTiles.length;
        const nextTiles = Array.from({ length: expectedLength }, (_, idx) => fallbackTiles[idx]);
        let newestUpdatedAtMs = 0;
        snap.docs.forEach((tileDoc) => {
          const data = tileDoc.data() || {};
          const pos = Number(data?.pos);
          const tileId = Number(data?.tileId);
          newestUpdatedAtMs = Math.max(newestUpdatedAtMs, Number(data?.updatedAtMs || 0));
          if (!Number.isFinite(pos) || pos < 0) return;
          nextTiles[pos] = Number.isFinite(tileId) ? tileId : nextTiles[pos];
        });
        const normalized = nextTiles.slice(0, expectedLength);
        setArePuzzleTilesLoaded(true);
        const remote = { tiles: normalized, updatedAtMs: newestUpdatedAtMs };
        applyRemoteTilesIfIdle(remote);
      },
      (err) => {
        console.error("Puzzle tiles snapshot failed:", err);
      }
    );
    return () => unsub();
  }, [gridSize, puzzleState?.tiles, puzzleTilesRef]);

  useEffect(() => {
    if (!puzzleLocksRef) return undefined;
    const unsub = onSnapshot(
      query(puzzleLocksRef, orderBy("pos", "asc")),
      (snap) => {
        if (snap.empty) {
          setPuzzleLockState(null);
          return;
        }
        const nextLocks = {};
        snap.docs.forEach((lockDoc) => {
          const data = lockDoc.data() || {};
          const pos = Number(data?.pos);
          if (!Number.isFinite(pos) || pos < 0) return;
          nextLocks[String(pos)] = data;
        });
        setPuzzleLockState(nextLocks);
      },
      (err) => {
        console.error("Puzzle locks snapshot failed:", err);
      }
    );
    return () => unsub();
  }, [puzzleLocksRef]);

  useEffect(() => {
    if (!lobbyDocRef) return undefined;
    const unsub = onSnapshot(
      lobbyDocRef,
      (snap) => {
        if (!snap.exists()) return;
        const status = String(snap.data()?.status || "").trim().toLowerCase();
        setLobbyStatus(status);
      },
      (err) => console.error("Lobby status snapshot failed:", err)
    );
    return () => unsub();
  }, [lobbyDocRef]);

  // Navigate to /leaderboard when the lobby flips to "gameover", but hold
  // the redirect while the kicked-out popup is being shown so the eliminated
  // player can read their result before the leaderboard takes over.
  useEffect(() => {
    if (lobbyStatus !== "gameover") return;
    if (showKickedPopup) return;
    navigate("/leaderboard");
  }, [lobbyStatus, showKickedPopup, navigate]);

  // Open the kicked-out popup the moment the local user transitions from
  // alive -> eliminated. We use a ref to detect the transition so reloading
  // an already-eliminated state (e.g. browser refresh) does not re-prompt.
  useEffect(() => {
    const wasEliminated = wasEliminatedRef.current;
    wasEliminatedRef.current = Boolean(isEliminatedPlayer);
    if (isEliminatedPlayer && !wasEliminated) {
      setShowKickedPopup(true);
    }
  }, [isEliminatedPlayer]);

  useEffect(() => {
    if (!sessionId || !lobbyName) return undefined;
    const usersRef = collection(database, `lobbies/${sessionId}/lobby/${lobbyName}/users`);
    const unsub = onSnapshot(
      query(usersRef, orderBy("timestamp", "asc")),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlayers(list);
        const me = list.find((p) => String(p?.userId || p?.id || "").trim() === userId);
        if (me) {
          setMyRole(String(me.role || "").trim());
          setMyActivity(String(me.activity || "idle"));
          setMyHeldTileId(
            me.heldTileId === null || me.heldTileId === undefined ? null : Number(me.heldTileId)
          );
          setMyHoldUntilMs(
            me.holdUntilMs === null || me.holdUntilMs === undefined ? null : Number(me.holdUntilMs)
          );
        }
      },
      (err) => console.error("Players snapshot failed:", err)
    );
    return () => unsub();
  }, [lobbyName, sessionId, userId]);

  useEffect(() => {
    if (!puzzlePresenceRef) return undefined;
    const unsub = onSnapshot(
      puzzlePresenceRef,
      (snap) => {
        const next = {};
	        snap.docs.forEach((presenceDoc) => {
	          const data = presenceDoc.data() || {};
	          const uid = String(data?.userId || presenceDoc.id || "").trim();
	          if (!uid) return;
	          const normalized = {
	            ...data,
	            userId: uid,
	            activity: String(data?.activity || "idle").trim().toLowerCase(),
            heldTileId:
              data?.heldTileId === null || data?.heldTileId === undefined
                ? null
                : Number(data?.heldTileId),
            holdUntilMs:
              data?.holdUntilMs === null || data?.holdUntilMs === undefined
                ? null
                : Number(data?.holdUntilMs),
	            updatedAtMs: Number(data?.updatedAtMs || 0),
	          };
	          next[uid] = normalized;
	          next[presenceDoc.id] = normalized;
	        });
        setPuzzlePresenceByUserId(next);
      },
      (err) => console.error("Puzzle presence snapshot failed:", err)
    );
    return () => unsub();
  }, [puzzlePresenceRef]);

  useEffect(() => {
    if (!chatCollectionRef) return undefined;
    const q = query(chatCollectionRef, orderBy("createdAtMs", "asc"), limit(20));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            userId: String(data?.userId || "").trim(),
            name: String(data?.name || "Player"),
            message: String(data?.message || ""),
            createdAtMs: Number(data?.createdAtMs || 0),
          };
        });
        setChatMessages(list);
      },
      (err) => console.error("Chat snapshot failed:", err)
    );
    return () => unsub();
  }, [chatCollectionRef]);

  useEffect(() => {
    if (myActivity === "holding" || myActivity === "dragging") return;
    const deferred = latestDeferredTilesRef.current;
    if (deferred) {
      applyRemoteTilesIfIdle(deferred);
    }
  }, [myActivity]);

  useEffect(() => {
    if (!isChatPopupOpen) return;
    if (!chatMessagesRef.current) return;
    chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
  }, [chatMessages, isChatPopupOpen]);

  useEffect(() => {
    if (!isChatPopupOpen) return;
    if (!chatMessages.length) return;
    const latest = chatMessages.reduce(
      (max, m) => Math.max(max, Number(m?.createdAtMs) || 0),
      0
    );
    if (latest > 0) {
      setLastSeenChatAtMs((prev) => (latest > prev ? latest : prev));
    }
  }, [chatMessages, isChatPopupOpen]);

  const unreadChatCount = useMemo(() => {
    if (isChatPopupOpen) return 0;
    return chatMessages.reduce((n, m) => {
      const t = Number(m?.createdAtMs || 0);
      const sender = String(m?.userId || "").trim();
      if (t > Number(lastSeenChatAtMs) && sender !== userId) return n + 1;
      return n;
    }, 0);
  }, [chatMessages, isChatPopupOpen, lastSeenChatAtMs, userId]);

  useEffect(() => {
    if (!puzzleTilesRef || !gridSize || tileSeedStartedRef.current) return;
    if (!Array.isArray(puzzleState?.tiles) || puzzleState.tiles.length !== gridSize * gridSize) return;
    if (!arePuzzleTilesLoaded) return;
    if (puzzleTilesState.length === gridSize * gridSize) return;
    tileSeedStartedRef.current = true;
    const seedTiles = async () => {
      try {
        await Promise.all(
          puzzleState.tiles.map((tileId, pos) =>
            setDoc(
              doc(puzzleTilesRef, String(pos)),
              {
                pos,
                tileId: Number(tileId),
                updatedAtMs: Date.now(),
              },
              { merge: true }
            )
          )
        );
      } catch (error) {
        console.error("Failed to seed puzzle tiles:", error);
        tileSeedStartedRef.current = false;
      }
    };
    seedTiles();
  }, [arePuzzleTilesLoaded, gridSize, puzzleState?.tiles, puzzleTilesRef, puzzleTilesState.length]);

  // Heartbeat: every 5 seconds, check if the user is still holding a
  // piece. If yes, refresh "dragging" so it does not expire. If no,
  // flip presence back to idle. This is the SOLE owner of the idle
  // transition outside of an actual drop commit, so transient
  // mouse/drag events never cause a mid-hold status flicker.
  useEffect(() => {
    if (!sessionId || !lobbyName || !userId || isEliminatedPlayer) return undefined;
    const interval = window.setInterval(() => {
      const stillHolding =
        isDraggingRef.current ||
        dragStartedRef.current ||
        Boolean(touchDragRef.current) ||
        dropInFlightRef.current ||
        Boolean(pendingMoveRef.current);
      if (stillHolding) {
        setMyStatusRef.current?.({
          activity: "dragging",
          heldTileId: dragTileIdRef.current,
          holdUntilMs: Date.now() + HOLD_TTL_MS,
        })?.catch?.(() => {});
        return;
      }
      // Not holding anything — only push idle if Firestore still
      // thinks we're active, to avoid a redundant write per tick.
      if (myActivity === "dragging" || myActivity === "holding") {
        setMyStatusRef.current?.({
          activity: "idle",
          heldTileId: null,
          holdUntilMs: null,
        })?.catch?.(() => {});
      }
    }, 2500);
    return () => window.clearInterval(interval);
  }, [sessionId, lobbyName, userId, isEliminatedPlayer, myActivity]);

  const setMyStatus = async (patch) => {
    if (!sessionId || !lobbyName || !userId) return;
    const normalizedPatch = {
      activity: String(patch?.activity || "idle").trim().toLowerCase(),
      heldTileId: patch?.heldTileId === undefined ? null : patch.heldTileId,
      holdUntilMs: patch?.holdUntilMs === undefined ? null : patch.holdUntilMs,
      updatedAtMs: Date.now(),
    };
    setLocalPuzzleActivity(normalizedPatch);
    setPuzzlePresenceByUserId((prev) => ({
      ...prev,
      [userId]: {
        ...(prev?.[userId] || {}),
        userId,
        ...normalizedPatch,
      },
    }));
    const meRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${userId}`);
    const presenceRef = doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/puzzlePresence/${userId}`);
    await Promise.all([
      setDoc(meRef, normalizedPatch, { merge: true }),
      setDoc(
        presenceRef,
        {
          ...normalizedPatch,
          userId,
          userName:
            stripNullWords(userData?.name) ||
            stripNullWords(userData?.displayName) ||
            String(userData?.email || "").trim() ||
            "Player",
        },
        { merge: true }
      ),
    ]);
  };

  setMyStatusRef.current = setMyStatus;

  const clearDragRefs = () => {
    isDraggingRef.current = false;
    dragFromPosRef.current = null;
    dragTileIdRef.current = null;
    dragStartedRef.current = false;
    touchDragRef.current = null;
    setTouchDrag(null);
  };

  // Just capture local drag refs without writing any presence/activity.
  // Used by onMouseDown so a quick click that never becomes a real drag
  // does not flicker our presence to "dragging" and back.
  const prepareLocalDragRefs = (pos, tileId) => {
    dragFromPosRef.current = pos;
    dragTileIdRef.current = tileId;
    isDraggingRef.current = true;
  };

  // Capture refs and write the "dragging" activity. Called only when a
  // real drag has actually begun (onDragStart on desktop, onPointerDown
  // on touch — touch has no separate dragstart event).
  const startLocalDragStatus = (pos, tileId) => {
    prepareLocalDragRefs(pos, tileId);
    setMyStatus({
      activity: "dragging",
      heldTileId: tileId,
      holdUntilMs: Date.now() + HOLD_TTL_MS,
    }).catch(() => {});
  };

  const releaseTileLock = async (pos, { force = false } = {}) => {
    if (!puzzleLocksRef || !userId) return;
    const p = Number(pos);
    if (!Number.isFinite(p) || p < 0) return;
    const lockRef = doc(puzzleLocksRef, String(p));
    const snap = await getDoc(lockRef);
    if (!snap.exists()) return;
    const data = snap.data() || {};
    const ownerId = String(data.userId || "").trim();
    const expired = Number(data.lockUntilMs || 0) <= Date.now();
    if (!(force || ownerId === userId || expired)) return;
    await setDoc(lockRef, { lockUntilMs: 0, releasedAtMs: Date.now() }, { merge: true });
  };

  const swapTiles = async (fromPos, toPos, draggedTileId, destinationTileId, moveMeta = {}) => {
    if (!puzzleTilesRef || !puzzleLocksRef) return;
    if (isEliminatedPlayer) return;
    const a = Number(fromPos);
    const b = Number(toPos);
    const dragged = Number(draggedTileId);
    const destination = Number(destinationTileId);
    if (!Number.isFinite(a) || !Number.isFinite(b) || a === b) return;
    if (!Number.isFinite(dragged) || !Number.isFinite(destination)) return;
    await runTransaction(database, async (tx) => {
      const fromTileRef = doc(puzzleTilesRef, String(a));
      const toTileRef = doc(puzzleTilesRef, String(b));
      const fromLockRef = doc(puzzleLocksRef, String(a));
      const toLockRef = doc(puzzleLocksRef, String(b));
      const [fromTileSnap, toTileSnap, lockSnap, dstLockSnap] = await Promise.all([
        tx.get(fromTileRef),
        tx.get(toTileRef),
        tx.get(fromLockRef),
        tx.get(toLockRef),
      ]);
      const currentFromTileId = Number(fromTileSnap.data()?.tileId);
      const currentToTileId = Number(toTileSnap.data()?.tileId);
      if (currentFromTileId !== dragged || currentToTileId !== destination) {
        const err = new Error("swap_precondition_failed");
        err.code = "swap_precondition_failed";
        throw err;
      }
      if (isRestrictedMover && (currentFromTileId === a || currentToTileId === b)) {
        const err = new Error("swap_role_restricted");
        err.code = "swap_role_restricted";
        throw err;
      }
      const srcLock = lockSnap.exists() ? lockSnap.data() || {} : null;
      const srcLockOwner = String(srcLock?.userId || "").trim();
      const now = Number(moveMeta?.commitAtMs || Date.now());
      tx.set(
        fromTileRef,
        {
          pos: a,
          tileId: destination,
          updatedAtMs: now,
          lastMoveId: String(moveMeta?.moveId || ""),
          lastMovedBy: userId,
        },
        { merge: true }
      );
      tx.set(
        toTileRef,
        {
          pos: b,
          tileId: dragged,
          updatedAtMs: now,
          lastMoveId: String(moveMeta?.moveId || ""),
          lastMovedBy: userId,
        },
        { merge: true }
      );
      if (srcLockOwner === userId) {
        tx.set(fromLockRef, { lockUntilMs: 0, releasedAtMs: now }, { merge: true });
      }
      const dstLock = dstLockSnap.exists() ? dstLockSnap.data() || {} : null;
      if (String(dstLock?.userId || "").trim() === userId) {
        tx.set(toLockRef, { lockUntilMs: 0, releasedAtMs: now }, { merge: true });
      }
    });
  };

  const swapTilesWithRetry = async (fromPos, toPos, draggedTileId, destinationTileId, moveMeta = {}) => {
    if (isEliminatedPlayer) return;
    for (let attempt = 0; attempt < SWAP_RETRY_LIMIT; attempt++) {
      try {
        await swapTiles(fromPos, toPos, draggedTileId, destinationTileId, moveMeta);
        return;
      } catch (error) {
        if (!isContentionError(error) || attempt >= SWAP_RETRY_LIMIT - 1) {
          throw error;
        }
        await sleep(30 + Math.floor(Math.random() * 60));
      }
    }
  };

  const commitOptimisticSwap = async ({ fromPos, toPos, tileId, destinationTileId, moveId, baseVersion, commitAtMs }) => {
    try {
      await swapTilesWithRetry(fromPos, toPos, tileId, destinationTileId, {
        moveId,
        clientAtMs: Date.now(),
        commitAtMs,
      });
      const pending = pendingMoveRef.current;
      if (pending && pending.moveId === moveId) {
        pendingMoveRef.current = null;
        setPuzzleTilesState(pending.expectedTiles);
        setOptimisticTiles(null);
      }
      setDropStatus("");
    } catch (err) {
      const errorMessage =
        err?.code === "swap_role_restricted"
          ? "Imposter and jester can't touch correctly placed pieces."
          : err?.code === "swap_precondition_failed"
            ? "Board changed, syncing..."
            : formatMoveError(err);
      console.warn("puzzle move rolled back", {
        code: err?.code,
        errorMessage,
        rawMessage: String(err?.message || err || ""),
        fromPos,
        toPos,
        tileId,
        destinationTileId,
        moveId,
        baseVersion,
      });
      const pending = pendingMoveRef.current;
      if (pending && pending.moveId === moveId) {
        pendingMoveRef.current = null;
        setDropStatus(errorMessage);
        setOptimisticTiles(null);
      }
      await releaseTileLock(fromPos, { force: true }).catch(() => {});
    }
    const deferred = latestDeferredTilesRef.current;
    if (!isLocalPuzzleInteractionActive() && deferred) {
      applyRemoteTilesIfIdle(deferred);
    }
  };

  const commitLocalDrop = async ({ toPos, tileId, fromPos }) => {
    if (isEliminatedPlayer) return false;
    if (pendingMoveRef.current) return false;
    const payloadTileId = Number(tileId);
    const destinationPos = Number(toPos);
    if (!Number.isFinite(payloadTileId) || !Number.isFinite(destinationPos)) return false;
    if (isTileLockedByOther(destinationPos)) return false;

    if (isRestrictedMover) {
      const sourceTilesView = Array.isArray(renderedTiles) ? renderedTiles : [];
      const payloadFromPosCheck = Number(fromPos);
      const sourceTileIdAtFrom =
        Number.isFinite(payloadFromPosCheck) && payloadFromPosCheck >= 0
          ? Number(sourceTilesView[payloadFromPosCheck])
          : payloadTileId;
      const destinationTileIdAtTo = Number(sourceTilesView[destinationPos]);
      if (
        isCorrectlyPlaced(payloadTileId, payloadFromPosCheck) ||
        isCorrectlyPlaced(sourceTileIdAtFrom, payloadFromPosCheck) ||
        isCorrectlyPlaced(destinationTileIdAtTo, destinationPos)
      ) {
        setDropStatus("Imposter and jester can't touch correctly placed pieces.");
        return false;
      }
    }

    dropInFlightRef.current = true;
    try {
      const sourceTiles = Array.isArray(renderedTiles) ? renderedTiles : [];
      const payloadFromPos = Number(fromPos);
      let from = -1;
      if (
        Number.isFinite(payloadFromPos) &&
        payloadFromPos >= 0 &&
        payloadFromPos < sourceTiles.length &&
        sourceTiles[payloadFromPos] === payloadTileId
      ) {
        from = payloadFromPos;
      } else {
        from = sourceTiles.indexOf(payloadTileId);
      }
      if (
        !Number.isFinite(from) ||
        from < 0 ||
        !sourceTiles.length ||
        from === destinationPos ||
        destinationPos < 0 ||
        destinationPos >= sourceTiles.length ||
        sourceTiles[from] !== payloadTileId
      ) {
        return false;
      }

      const nextTiles = [...sourceTiles];
      const destinationTileId = nextTiles[destinationPos];
      const tmp = nextTiles[from];
      nextTiles[from] = nextTiles[destinationPos];
      nextTiles[destinationPos] = tmp;
      const moveStartedAt = Date.now();
      const moveId = `${userId}-${moveStartedAt}-${Math.floor(Math.random() * 100000)}`;
      const baseVersion = Number(puzzleState?.version || 0);
      const commitAtMs = Date.now();
      pendingMoveRef.current = {
        moveId,
        fromPos: from,
        toPos: destinationPos,
        tileId: payloadTileId,
        destinationTileId,
        expectedTiles: nextTiles,
        baseVersion,
        startedAtMs: moveStartedAt,
        commitAtMs,
      };
      setOptimisticTiles(nextTiles);
      setPuzzleTilesState(nextTiles);
      setDropStatus("");
      await commitOptimisticSwap({
        fromPos: from,
        toPos: destinationPos,
        tileId: payloadTileId,
        destinationTileId,
        moveId,
        baseVersion,
        commitAtMs,
      });
      return true;
    } finally {
      clearDragRefs();
      dropInFlightRef.current = false;
      const deferred = latestDeferredTilesRef.current;
      if (deferred) {
        applyRemoteTilesIfIdle(deferred);
      }
      setMyStatus({ activity: "idle", heldTileId: null, holdUntilMs: null }).catch(() => {});
    }
  };

  const closeVotingPopup = () => {
    if (isVotingForcedOpen) return;
    setIsVotingPopupOpen(false);
  };

  const openChatPopup = (options = {}) => {
    const fromVoting = Boolean(options?.fromVoting);
    // From the main puzzle actions, close voting so only one modal is open.
    // From inside the voting modal, keep voting open so chat stacks on top.
    if (!fromVoting) {
      setIsVotingPopupOpen(false);
    }
    setIsChatPopupOpen(true);
  };

  const closeChatPopup = () => {
    setIsChatPopupOpen(false);
  };

  const formatChatTime = (ms) => {
    const date = new Date(Number(ms) || Date.now());
    let h = date.getHours();
    const m = String(date.getMinutes()).padStart(2, "0");
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${String(h).padStart(2, "0")}:${m} ${ampm}`;
  };

  const sendChatMessage = async () => {
    if (!chatCollectionRef || sendingChat) return;
    const text = chatInput.trim();
    if (!text) return;
    setSendingChat(true);
    try {
      await addDoc(chatCollectionRef, {
        name:
          stripNullWords(userData?.name) ||
          stripNullWords(userData?.displayName) ||
          String(userData?.email || "").trim() ||
          "Player",
        userId,
        message: text,
        createdAtMs: Date.now(),
      });
      setChatInput("");
    } catch (err) {
      console.error("sendChatMessage failed:", err);
    } finally {
      setSendingChat(false);
    }
  };

  const handleResetTime = async () => {
    if (!puzzleStateRef) return;
    try {
      const now = Date.now();
      await setDoc(
        puzzleStateRef,
        {
          startedAtMs: now,
          updatedAtMs: now,
        },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to reset puzzle timer:", err);
    }
  };

  const resolveVotingOutcome = (votesMap, voterRows, { requireAllVotes = true } = {}) => {
    const voterCount = voterRows.length;
    const totalVotes = Object.values(votesMap || {})
      .map((v) => String(v || "").trim())
      .filter(Boolean);
    const nonSkipVotes = totalVotes.filter((v) => v !== "skip");
    if (!voterCount || (requireAllVotes && totalVotes.length < voterCount)) {
      return { ended: false, reason: "pending" };
    }
    if (!nonSkipVotes.length) {
      return { ended: false, reason: "all_skip" };
    }
    const counts = {};
    for (const targetId of nonSkipVotes) counts[targetId] = (counts[targetId] || 0) + 1;
    const entries = Object.entries(counts);
    if (!entries.length) return { ended: false, reason: "all_skip" };
    const maxVotes = Math.max(...entries.map(([, c]) => Number(c)));
    const topCandidates = entries.filter(([, c]) => Number(c) === maxVotes).map(([uid]) => uid);
    if (maxVotes <= voterCount / 2) {
      return { ended: false, reason: "no_majority" };
    }
    if (topCandidates.length !== 1) {
      return { ended: false, reason: "tie" };
    }
    const eliminatedUserId = topCandidates[0];
    const eliminated = voterRows.find((p) => String(p?.userId || p?.id || "").trim() === eliminatedUserId);
    const eliminatedRole = String(eliminated?.role || "").trim().toLowerCase();

    if (eliminatedRole === "imposter") {
      return {
        ended: false,
        reason: "imposter_voted_out",
        eliminatedUserId,
        eliminatedRole,
        winnerRoleGroup: "",
        winnerUserIds: [],
      };
    }
    if (eliminatedRole === "jester") {
      const winnerUserIds = voterRows
        .filter((p) => String(p?.role || "").trim().toLowerCase() === "jester")
        .map((p) => String(p?.userId || p?.id || "").trim())
        .filter(Boolean);
      return {
        ended: true,
        reason: "jester_win",
        eliminatedUserId,
        eliminatedRole,
        winnerRoleGroup: "jester",
        winnerUserIds,
      };
    }
    return {
      ended: false,
      reason: "normal_voted_out",
      eliminatedUserId,
      eliminatedRole,
      winnerRoleGroup: "",
      winnerUserIds: [],
    };
  };

  // If two non-imposter, non-jester players have been voted out (this
  // round's elimination counts toward that total), the imposter wins.
  const applyImposterDoubleOutRule = (outcome) => {
    if (!outcome || outcome.reason !== "normal_voted_out") return outcome;
    const priorNormalEliminations = (players || []).filter((p) => {
      const status = String(p?.status || "").trim().toLowerCase();
      const role = String(p?.role || "").trim().toLowerCase();
      return (
        status === "eliminated" &&
        (role === "player" || role === "normal")
      );
    }).length;
    if (priorNormalEliminations + 1 < 2) return outcome;
    const imposterIds = (players || [])
      .filter((p) => String(p?.role || "").trim().toLowerCase() === "imposter")
      .map((p) => String(p?.userId || p?.id || "").trim())
      .filter(Boolean);
    return {
      ...outcome,
      ended: true,
      reason: "imposter_two_normals_out_win",
      winnerRoleGroup: "imposter",
      winnerUserIds: imposterIds,
    };
  };

  const submitVote = async (overrideTarget) => {
    // overrideTarget lets the Skip Vote button submit "skip" in one click
    // without waiting for selectedVoteTarget state to flush.
    const target = String(overrideTarget || selectedVoteTarget || "").trim();
    if (!puzzleStateRef || !target || !userId || !activePlayers.length || isEliminatedPlayer) return;
    try {
      await runTransaction(database, async (tx) => {
        const snap = await tx.get(puzzleStateRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const voting = data?.voting && typeof data.voting === "object" ? { ...data.voting } : {};
        const votes =
          voting?.votesByUserId && typeof voting.votesByUserId === "object"
            ? { ...voting.votesByUserId }
            : {};
        const priorResult = voting?.result && typeof voting.result === "object" ? voting.result : null;
        if (!voting?.isOpen || priorResult) return;
        if (String(votes[userId] || "").trim()) return;

        votes[userId] = target;
	        const voterIds = activePlayers
	          .map((p) => String(p?.userId || p?.id || "").trim())
	          .filter(Boolean);
        const now = Date.now();
        const allVoted = voterIds.every((uid) => Boolean(String(votes[uid] || "").trim()));
        const nextVoting = {
          ...voting,
          votesByUserId: votes,
          updatedAtMs: now,
        };
        if (allVoted) {
	          const outcome = applyImposterDoubleOutRule(
	            resolveVotingOutcome(votes, activePlayers)
	          );
          nextVoting.isOpen = false;
          nextVoting.closedAtMs = now;
          nextVoting.cooldownUntilMs =
            outcome.reason === "imposter_voted_out" ? 0 : now + VOTING_COOLDOWN_SEC * 1000;
          nextVoting.result = {
            ended: Boolean(outcome.ended),
            reason: outcome.reason || "",
            eliminatedUserId: outcome.eliminatedUserId || "",
            eliminatedRole: outcome.eliminatedRole || "",
            winnerRoleGroup: outcome.winnerRoleGroup || "",
            winnerUserIds: Array.isArray(outcome.winnerUserIds) ? outcome.winnerUserIds : [],
            resolvedAtMs: now,
          };
          if (outcome.eliminatedUserId) {
            tx.set(
              doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${outcome.eliminatedUserId}`),
              {
                status: "eliminated",
                eliminatedAtMs: now,
                eliminatedReason: outcome.reason || "",
                activity: "idle",
                heldTileId: null,
                holdUntilMs: null,
              },
              { merge: true }
            );
          }
          if (outcome.ended && lobbyDocRef) {
            tx.set(lobbyDocRef, { status: "gameover", gameoverAt: now }, { merge: true });
            nextVoting.finalOutcome = {
              winnerUserIds: Array.isArray(outcome.winnerUserIds) ? outcome.winnerUserIds : [],
              winnerRoleGroup: outcome.winnerRoleGroup || "",
              reason: outcome.reason || "",
              resolvedAtMs: now,
            };
            tx.set(
              lobbyDocRef,
              {
                winnerSummary: {
                  winnerUserIds: Array.isArray(outcome.winnerUserIds) ? outcome.winnerUserIds : [],
                  winnerRoleGroup: outcome.winnerRoleGroup || "",
                  reason: outcome.reason || "",
                  resolvedAtMs: now,
                },
              },
              { merge: true }
            );
          }
        }

        tx.set(puzzleStateRef, { voting: nextVoting, updatedAtMs: now }, { merge: true });
      });
    } catch (err) {
      console.error("submitVote failed:", err);
    }
  };

  const startVotingRound = async () => {
    if (!puzzleStateRef) return;
    try {
      const now = Date.now();
      await runTransaction(database, async (tx) => {
        const snap = await tx.get(puzzleStateRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const existing = data?.voting && typeof data.voting === "object" ? data.voting : {};
        const startedAtMs = Number(data?.startedAtMs || 0);
        const totalSec = Number(data?.totalTimeSec || 0);
        const startTimeSec = Math.max(
          0,
          Math.min(totalSec || 0, Number(data?.votingStartTimeSec ?? 8 * 60))
        );
        const unlockAtElapsed = Math.max(0, totalSec - startTimeSec);
        const durationSec = Math.max(1, Number(data?.votingDurationSec || 60));
        const elapsed = startedAtMs && totalSec ? Math.max(0, Math.floor((now - startedAtMs) / 1000)) : 0;
        const cooldownUntil = Number(existing?.cooldownUntilMs || 0);
        if (existing?.isOpen) return;
        if (elapsed < unlockAtElapsed) return;
        if (cooldownUntil > now) return;
        tx.set(
          puzzleStateRef,
          {
            voting: {
              ...existing,
              isOpen: true,
              startedAtMs: now,
              endsAtMs: now + durationSec * 1000,
              durationSec,
              closedAtMs: 0,
              cooldownUntilMs: 0,
              votesByUserId: {},
              result: null,
              reportSyncInProgress: false,
              finalReportSynced: false,
              finalReportSyncedBy: "",
              finalReportSyncedAt: 0,
            },
            updatedAtMs: now,
          },
          { merge: true }
        );
      });
      setSelectedVoteTarget("");
      setIsChatPopupOpen(false);
      setIsVotingPopupOpen(true);
    } catch (err) {
      console.error("Failed to start voting round:", err);
    }
  };

  const closeVotingRound = async ({ requireAllVotes = false } = {}) => {
    if (!puzzleStateRef) return;
    const now = Date.now();
    try {
      await runTransaction(database, async (tx) => {
        const snap = await tx.get(puzzleStateRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const voting = data?.voting && typeof data.voting === "object" ? { ...data.voting } : {};
        if (!voting?.isOpen) return;
        const durationSec = Math.max(1, Number(voting?.durationSec || data?.votingDurationSec || 60));
        const endsAt = Number(voting?.endsAtMs || 0) || Number(voting?.startedAtMs || 0) + durationSec * 1000;
        if (requireAllVotes === false && endsAt > now) return;
        const votes =
          voting?.votesByUserId && typeof voting.votesByUserId === "object"
            ? { ...voting.votesByUserId }
            : {};
        const outcome = applyImposterDoubleOutRule(
          resolveVotingOutcome(votes, activePlayers, { requireAllVotes })
        );
        voting.isOpen = false;
        voting.closedAtMs = now;
        voting.cooldownUntilMs =
          outcome.reason === "imposter_voted_out" ? 0 : now + VOTING_COOLDOWN_SEC * 1000;
        voting.result = {
          ended: Boolean(outcome.ended),
          reason: outcome.reason === "pending" ? "timeout" : outcome.reason || "timeout",
          eliminatedUserId: outcome.eliminatedUserId || "",
          eliminatedRole: outcome.eliminatedRole || "",
          winnerRoleGroup: outcome.winnerRoleGroup || "",
          winnerUserIds: Array.isArray(outcome.winnerUserIds) ? outcome.winnerUserIds : [],
          resolvedAtMs: now,
        };
        if (outcome.ended && lobbyDocRef) {
          voting.finalOutcome = {
            winnerUserIds: Array.isArray(outcome.winnerUserIds) ? outcome.winnerUserIds : [],
            winnerRoleGroup: outcome.winnerRoleGroup || "",
            reason: outcome.reason || "",
            resolvedAtMs: now,
          };
          tx.set(
            lobbyDocRef,
            {
              status: "gameover",
              gameoverAt: now,
              winnerSummary: voting.finalOutcome,
            },
            { merge: true }
          );
        }
        if (outcome.eliminatedUserId) {
          tx.set(
            doc(database, `lobbies/${sessionId}/lobby/${lobbyName}/users/${outcome.eliminatedUserId}`),
            {
              status: "eliminated",
              eliminatedAtMs: now,
              eliminatedReason: outcome.reason || "",
              activity: "idle",
              heldTileId: null,
              holdUntilMs: null,
            },
            { merge: true }
          );
        }
        tx.set(puzzleStateRef, { voting, updatedAtMs: now }, { merge: true });
      });
    } catch (err) {
      console.error("Failed to close voting round:", err);
    }
  };

  const openVotingPopup = () => {
    if (votingState?.isOpen) {
      setIsChatPopupOpen(false);
      setIsVotingPopupOpen(true);
      return;
    }
    if (!canStartVoting) return;
    startVotingRound();
  };

  useEffect(() => {
    if (!isVotingForcedOpen) return;
    setIsChatPopupOpen(false);
    setIsVotingPopupOpen(true);
  }, [isVotingForcedOpen]);

  useEffect(() => {
    if (votingState?.isOpen) return;
    if (isVotingPopupOpen && result) {
      setIsVotingPopupOpen(false);
    }
  }, [isVotingPopupOpen, result, votingState?.isOpen]);

  useEffect(() => {
    if (!votingState?.isOpen || !votingEndsAtMs) return undefined;
    const delay = Math.max(0, votingEndsAtMs - Date.now());
    const timer = window.setTimeout(() => {
      closeVotingRound({ requireAllVotes: false });
    }, delay + 100);
    return () => window.clearTimeout(timer);
  }, [votingState?.isOpen, votingEndsAtMs]);

  useEffect(() => {
    if (!puzzleStateRef || !lobbyDocRef) return;
    if (finalOutcome) return;
    const startedAtMs = Number(puzzleState?.startedAtMs || 0);
    const totalTimeSecVal = Number(puzzleState?.totalTimeSec || 0);
    if (!startedAtMs || !totalTimeSecVal) return;
    if (!totalTiles) return;
    const now = Date.now();
    const puzzleCompleted = solvedCount === totalTiles;
    const timerExpired = remainingSec <= 0;
    if (!puzzleCompleted && !timerExpired) return;

    const isImposter = (p) => String(p?.role || "").trim().toLowerCase() === "imposter";
    const isJester = (p) => String(p?.role || "").trim().toLowerCase() === "jester";
    const imposterIds = livePlayers
      .filter(isImposter)
      .map((p) => String(p?.userId || p?.id || "").trim())
      .filter(Boolean);
    const winnerUserIds = timerExpired
      ? imposterIds
      : // Players-win: include ALL player-role users even if some were
        // voted out earlier — the team still wins together.
        livePlayers
          .filter((p) => !isImposter(p) && !isJester(p))
          .map((p) => String(p?.userId || p?.id || "").trim())
          .filter(Boolean);
    const winnerRoleGroup = timerExpired ? "imposter" : "others";
    const reason = timerExpired ? "imposter_timeout_win" : "others_puzzle_completed";

    (async () => {
      try {
        await setDoc(
          puzzleStateRef,
          {
            voting: {
              ...votingState,
              finalOutcome: {
                winnerUserIds,
                winnerRoleGroup,
                reason,
                resolvedAtMs: now,
              },
            },
          },
          { merge: true }
        );
        await setDoc(
          lobbyDocRef,
          {
            status: "gameover",
            gameoverAt: now,
            winnerSummary: {
              winnerUserIds,
              winnerRoleGroup,
              reason,
              resolvedAtMs: now,
            },
          },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to finalize puzzle outcome:", e);
      }
    })();
  }, [
	    finalOutcome,
	    activePlayers,
	    livePlayers,
    lobbyDocRef,
    puzzleStateRef,
    puzzleState?.startedAtMs,
    puzzleState?.totalTimeSec,
    remainingSec,
    solvedCount,
    totalTiles,
    votingState,
  ]);

  // Theme + copy for the kicked-out popup, keyed off the eliminated user's
  // role. Mirrors the role-reveal popup palette (imposter red, jester blue
  // with yellow accents, regular player blue).
  const kickedConfig = useMemo(() => {
    const reason = String(result?.reason || "").trim().toLowerCase();
    if (kickedRole === "imposter") {
      return {
        themeClass: "kicked-popup-imposter",
        title: "BUSTED",
        message: "The team voted you out. The imposter has been caught.",
      };
    }
    if (kickedRole === "jester") {
      return {
        themeClass: "kicked-popup-jester",
        title: "JESTER WINS",
        message:
          "Mission accomplished — you got yourself voted out and stole the win.",
      };
    }
    return {
      themeClass: "kicked-popup-player",
      title: "VOTED OUT",
      message:
        reason === "jester_win"
          ? "You voted out the jester by mistake — the round goes to them."
          : "You were voted out, but you were not the imposter.",
    };
  }, [kickedRole, result?.reason]);

  const handleKickedDismiss = () => {
    setShowKickedPopup(false);
  };

  return (
    <div className="puzzle-page">
      <div className="puzzle-layout">
        <section className="puzzle-board-panel">
          <div className="puzzle-board-wrap">
            {gridSize && renderedTiles.length === totalTiles ? (
              <div
                className="puzzle-grid"
                style={{
                  ["--puzzle-grid-size"]: gridSize,
                  ["--puzzle-image-url"]: `url("${imageSrc}")`,
                }}
              >
	                {renderedTiles.map((tileId, pos) => {
	                  const tileRow = Math.floor(tileId / gridSize);
	                  const tileCol = tileId % gridSize;
	                  const isTouchDraggingTile = touchDrag?.fromPos === pos;
	                  // Identify which player is currently dragging *this*
	                  // tile, if any. We drive this off presence (which is
	                  // already broadcast on dragstart / pointerdown) instead
	                  // of puzzleLocks because nothing in the codebase ever
	                  // acquires a lock — it would always be empty.
	                  let draggerId = "";
	                  for (const _p of players) {
	                    const _uid = String(_p?.userId || _p?.id || "").trim();
	                    if (!_uid) continue;
	                    const _pres =
	                      _uid === userId
	                        ? localPuzzleActivity
	                        : puzzlePresenceByUserId[_uid];
	                    if (!_pres) continue;
	                    if (
	                      String(_pres.activity || "").toLowerCase() !== "dragging"
	                    )
	                      continue;
	                    if (Number(_pres.holdUntilMs || 0) <= nowMs) continue;
	                    if (Number(_pres.heldTileId) !== Number(tileId)) continue;
	                    draggerId = _uid;
	                    break;
	                  }
	                  const isDragOwnerMe = Boolean(draggerId) && draggerId === userId;
	                  const dragColor = draggerId
	                    ? playerColorById[draggerId] || null
	                    : null;
	                  const draggingByAttr = draggerId
	                    ? isDragOwnerMe
	                      ? "me"
	                      : "other"
	                    : "false";
	                  return (
	                    <div
	                      key={`${pos}-${tileId}`}
	                      className="puzzle-tile"
	                      data-puzzle-pos={pos}
	                      draggable={
	                        !isEliminatedPlayer &&
	                        !(isRestrictedMover && isCorrectlyPlaced(tileId, pos)) &&
	                        !(
	                          lockByPos[String(pos)] &&
	                          String(lockByPos[String(pos)]?.userId || "").trim() !== userId
	                        )
	                      }
                      onMouseDown={() => {
                        if (isEliminatedPlayer || isTileLockedByOther(pos)) return;
                        if (isRestrictedMover && isCorrectlyPlaced(tileId, pos)) return;
                        // Fresh gesture: clear any leftover dragstart flag.
                        dragStartedRef.current = false;
                        // Flip presence to "dragging" immediately so the
                        // player card and the tile border update right away.
                        // The synthetic mouseup that the browser emits once a
                        // native drag actually starts is filtered out below
                        // via dragStartedRef.
                        startLocalDragStatus(pos, tileId);
                      }}
                      onMouseUp={() => {
                        // Just clear local drag refs. The 5s heartbeat
                        // owns the idle transition, so we do NOT write
                        // "idle" here — that was the source of mid-hold
                        // status flickers.
                        if (dragStartedRef.current) return;
                        if (touchDragRef.current) return;
                        if (!dropInFlightRef.current && dragFromPosRef.current === pos) {
                          clearDragRefs();
                        }
                      }}
                      onDragStart={async (e) => {
                        if (isEliminatedPlayer) {
                          e.preventDefault();
                          return;
                        }
                        if (isRestrictedMover && isCorrectlyPlaced(tileId, pos)) {
                          e.preventDefault();
                          setDropStatus("Imposter and jester can't move correctly placed pieces.");
                          return;
                        }
                        // Canonical payload is written synchronously before async work.
                        const payload = { fromPos: pos, tileId };
                        try {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData(DRAG_PAYLOAD_TYPE, JSON.stringify(payload));
                          e.dataTransfer.setData("text/plain", JSON.stringify(payload));
                        } catch {
                          // ignore
                        }
                        const currentLock = lockByPos[String(pos)];
                        if (
                          currentLock &&
                          String(currentLock?.userId || "").trim() !== userId
                        ) {
                          e.preventDefault();
                          return;
                        }
                        // Mark that a native drag is now active so the
                        // synthetic mouseup that some browsers fire right
                        // after dragstart is ignored by onMouseUp.
                        dragStartedRef.current = true;
                        startLocalDragStatus(pos, tileId);
                      }}
                      onDragEnd={async () => {
                        const fromPos = dragFromPosRef.current;
                        // If a drop is being processed, commitLocalDrop's
                        // finally will reset status; don't race with it.
                        if (dropInFlightRef.current) return;
                        clearDragRefs();
                        if (Number.isFinite(Number(fromPos))) {
                          releaseTileLock(fromPos).catch(() => {});
                        }
                        // No idle write here — the 5s heartbeat will
                        // flip presence to idle once it sees the user
                        // is no longer holding anything.
                      }}
	                      onPointerDown={(e) => {
	                        if (e.pointerType === "mouse") return;
	                        if (isEliminatedPlayer || isTileLockedByOther(pos)) return;
	                        if (isRestrictedMover && isCorrectlyPlaced(tileId, pos)) {
	                          setDropStatus("Imposter and jester can't move correctly placed pieces.");
	                          return;
	                        }
	                        e.preventDefault();
	                        try {
	                          e.currentTarget.setPointerCapture(e.pointerId);
	                        } catch {
	                          // ignore
	                        }
	                        // Capture the tile's size + finger offset within the
	                        // tile so the floating ghost can follow the finger
	                        // at the exact spot it was grabbed (mirrors the
	                        // browser's native HTML5 drag image on desktop).
	                        const rect = e.currentTarget.getBoundingClientRect();
	                        const nextTouchDrag = {
	                          pointerId: e.pointerId,
	                          fromPos: pos,
	                          tileId,
	                          startX: e.clientX,
	                          startY: e.clientY,
	                          currentX: e.clientX,
	                          currentY: e.clientY,
	                          width: rect.width,
	                          height: rect.height,
	                          offsetX: e.clientX - rect.left,
	                          offsetY: e.clientY - rect.top,
	                          tileRow: Math.floor(tileId / gridSize),
	                          tileCol: tileId % gridSize,
	                        };
	                        touchDragRef.current = nextTouchDrag;
	                        setTouchDrag(nextTouchDrag);
	                        startLocalDragStatus(pos, tileId);
	                      }}
	                      onPointerMove={(e) => {
	                        const active = touchDragRef.current;
	                        if (!active || active.pointerId !== e.pointerId) return;
	                        e.preventDefault();
	                        const nextTouchDrag = {
	                          ...active,
	                          currentX: e.clientX,
	                          currentY: e.clientY,
	                        };
	                        touchDragRef.current = nextTouchDrag;
	                        setTouchDrag(nextTouchDrag);
	                      }}
	                      onPointerUp={async (e) => {
	                        const active = touchDragRef.current;
	                        if (!active || active.pointerId !== e.pointerId) return;
	                        e.preventDefault();
	                        try {
	                          e.currentTarget.releasePointerCapture(e.pointerId);
	                        } catch {
	                          // ignore
	                        }
	                        const targetPos = (() => {
	                          const target = document
	                            .elementFromPoint(e.clientX, e.clientY)
	                            ?.closest("[data-puzzle-pos]");
	                          return target ? Number(target.getAttribute("data-puzzle-pos")) : -1;
	                        })();
	                        // Drop the floating ghost and the source-tile dim
	                        // immediately so the optimistic swap renders cleanly
	                        // while the Firestore transaction syncs in the
	                        // background. clearDragRefs in commitLocalDrop's
	                        // finally is idempotent on these.
	                        touchDragRef.current = null;
	                        setTouchDrag(null);
	                        if (Number.isFinite(targetPos) && targetPos >= 0) {
	                          await commitLocalDrop({
	                            toPos: targetPos,
	                            tileId: active.tileId,
	                            fromPos: active.fromPos,
	                          });
	                          return;
	                        }
	                        clearDragRefs();
	                      }}
	                      onPointerCancel={() => {
	                        clearDragRefs();
	                      }}
	                      onDragOver={(e) => e.preventDefault()}
	                      onDrop={async (e) => {
	                        e.preventDefault();
	                        if (isEliminatedPlayer) return;
	                        const rawPayload = (() => {
	                          try {
	                            return (
	                              e.dataTransfer.getData(DRAG_PAYLOAD_TYPE) ||
	                              e.dataTransfer.getData("text/plain")
	                            );
	                          } catch {
	                            return "";
	                          }
	                        })();
	                        let parsedPayload = null;
	                        if (rawPayload) {
	                          try {
	                            parsedPayload = JSON.parse(rawPayload);
	                          } catch {
	                            parsedPayload = null;
	                          }
	                        }
	                        const payloadTileId =
	                          parsedPayload?.tileId === null || parsedPayload?.tileId === undefined
	                            ? Number(dragTileIdRef.current)
	                            : Number(parsedPayload.tileId);
	                        await commitLocalDrop({
	                          toPos: pos,
	                          tileId: payloadTileId,
	                          fromPos: parsedPayload?.fromPos,
	                        });
	                      }}
                      title={
                        lockByPos[String(pos)] &&
                        String(lockByPos[String(pos)]?.userId || "").trim() !== userId
                          ? `Locked by ${lockByPos[String(pos)]?.userName || "another player"}`
                          : ""
                      }
	                      style={{
	                        ["--tile-row"]: tileRow,
	                        ["--tile-col"]: tileCol,
	                        ...(dragColor ? { ["--lock-drag-color"]: dragColor } : {}),
	                      }}
	                      data-touch-dragging={isTouchDraggingTile ? "true" : "false"}
	                      data-dragging-by={draggingByAttr}
                      data-locked-by-other={
                        lockByPos[String(pos)] &&
                        String(lockByPos[String(pos)]?.userId || "").trim() !== userId
                          ? "true"
                          : "false"
                      }
                      data-locked-by-me={
                        lockByPos[String(pos)] &&
                        String(lockByPos[String(pos)]?.userId || "").trim() === userId
                          ? "true"
                          : "false"
                      }
                      data-correct={tileId === pos ? "true" : "false"}
                      aria-label={`Tile ${pos + 1}`}
                    />
                  );
                })}
              </div>
            ) : (
              <img src={imageSrc} alt="Puzzle board" className="puzzle-board-image" />
            )}
            {touchDrag && gridSize ? (
              <div
                className="puzzle-touch-ghost"
                aria-hidden="true"
                style={{
                  width: `${touchDrag.width}px`,
                  height: `${touchDrag.height}px`,
                  backgroundImage: `url("${imageSrc}")`,
                  backgroundSize: `calc(${gridSize} * 100%) calc(${gridSize} * 100%)`,
                  backgroundPosition: `calc(${touchDrag.tileCol} * (100% / (${gridSize} - 1))) calc(${touchDrag.tileRow} * (100% / (${gridSize} - 1)))`,
                  transform: `translate3d(${
                    touchDrag.currentX - touchDrag.offsetX
                  }px, ${touchDrag.currentY - touchDrag.offsetY}px, 0) scale(1.06)`,
                }}
              />
            ) : null}
          </div>
        </section>

        <section className="puzzle-side-panel">
          <div className="puzzle-progress-card">
            <div className="puzzle-progress-header">
              <h2>Puzzle Progress</h2>
              <div className="puzzle-timer-actions">
                <span>{remainingLabel}</span>
                <button type="button" className="puzzle-temp-reset-btn" onClick={handleResetTime}>
                  Reset Time
                </button>
              </div>
            </div>
            <div className="puzzle-progress-track">
              <div className="puzzle-progress-fill" style={{ width: `${progressPct}%` }} />
              <div className="puzzle-progress-label">{progressPct}%</div>
            </div>
            {myRole ? <div className="puzzle-role">Role: {myRole}</div> : null}
            {dropStatus ? <div className="puzzle-role">{dropStatus}</div> : null}
	            {eliminationNotices.map((msg, i) => (
	              <div className="puzzle-vote-notice" key={`elim-${i}`}>{msg}</div>
	            ))}
	            {voteResultNonEliminationMessage ? (
	              <div className="puzzle-vote-notice">{voteResultNonEliminationMessage}</div>
	            ) : null}
	            {finalOutcomeMessage ? (
	              <div className="puzzle-vote-notice">{finalOutcomeMessage}</div>
	            ) : null}
	            {isEliminatedPlayer ? (
	              <div className="puzzle-vote-notice is-warning">
	                You were voted out. You can no longer play puzzle.
	              </div>
	            ) : null}
          </div>

	          <div className="puzzle-players-grid">
	            {(players || []).slice(0, 4).map((p, idx) => {
	              const playerKeys = getPlayerIdentityKeys(p);
	              const isMe = playerKeys.includes(userId);
	              const presence = isMe
	                ? localPuzzleActivity
	                : playerKeys.map((key) => puzzlePresenceByUserId[key]).find(Boolean) || null;
	              const presenceActive =
	                presence &&
	                (Number(presence.holdUntilMs || 0) > nowMs ||
	                  Number(presence.updatedAtMs || 0) > nowMs - HOLD_TTL_MS);
	              const name = resolvePlayerName(p, idx + 1);
	              const act = String(
	                presenceActive ? presence?.activity || "idle" : p?.activity || "idle"
	              )
	                .trim()
	                .toLowerCase();
	              const className =
                act === "dragging"
                  ? "puzzle-player-card accent-green"
                  : act === "holding"
                  ? "puzzle-player-card accent-pink"
                  : "puzzle-player-card";
              const label =
                act === "dragging" ? "Dragging Piece" : act === "holding" ? "Holding Piece" : "Idle";
              const playerColor =
                playerKeys.map((key) => playerColorById[key]).find(Boolean) || null;
              return (
                <article
                  key={p?.userId || p?.id || idx}
                  className={className}
                  style={playerColor ? { ["--player-color"]: playerColor } : undefined}
                >
                  <h3>{name}</h3>
                  <p>{label}</p>
                </article>
              );
            })}
          </div>

          <div className="puzzle-actions">
            <button type="button" className="puzzle-action-btn" onClick={openChatPopup}>
              Quick Chat
              {unreadChatCount > 0 ? (
                <span
                  className="puzzle-chat-unread-badge"
                  aria-label={`${unreadChatCount} unread message${unreadChatCount === 1 ? "" : "s"}`}
                >
                  {unreadChatCount > 9 ? "9+" : unreadChatCount}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className={`puzzle-action-btn voting-btn${canStartVoting ? " is-ready" : ""}${
                votingState?.isOpen ? " is-active" : ""
              }`}
              onClick={openVotingPopup}
              disabled={!canStartVoting && !votingState?.isOpen}
            >
              <span>Voting</span>
              <small>{votingButtonStatus}</small>
            </button>
          </div>
        </section>
      </div>

      {isVotingPopupOpen ? (
        <div className="puzzle-voting-overlay" onClick={closeVotingPopup} role="presentation">
          <div
            className="puzzle-voting-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Voting popup"
          >
            {!isVotingForcedOpen ? (
              <button type="button" className="puzzle-voting-close" onClick={closeVotingPopup} aria-label="Close voting popup">
                X
              </button>
            ) : null}

            <div className="puzzle-voting-toolbar">
              <button
                type="button"
                className="puzzle-voting-quick-chat-btn"
                onClick={() => openChatPopup({ fromVoting: true })}
                aria-label="Open quick chat"
              >
                Quick Chat
                {unreadChatCount > 0 && !isChatPopupOpen ? (
                  <span
                    className="puzzle-chat-unread-badge"
                    aria-label={`${unreadChatCount} unread message${unreadChatCount === 1 ? "" : "s"}`}
                  >
                    {unreadChatCount > 9 ? "9+" : unreadChatCount}
                  </span>
                ) : null}
              </button>
            </div>

            <div className="puzzle-voting-head">
              <h3>Voting Time</h3>
              <span className="puzzle-voting-spinner" aria-hidden="true" />
            </div>
            <p className="puzzle-voting-subtitle">
              Guess who is the imposter · {formatDurationFromSeconds(votingRemainingSec)}
            </p>

            <div className="puzzle-voting-players">
	              {(activePlayers || []).map((p, idx) => {
                const pid = String(p?.userId || p?.id || "").trim();
                const pname = resolvePlayerName(p, idx + 1);
                const selected = selectedVoteTarget === pid;
                const character = String(p?.character || "").trim().toLowerCase();
                return (
                  <article
                    key={`vote-${pid || idx}`}
                    className={`puzzle-voting-player-card${selected ? " is-selected" : ""}`}
                    role="button"
	                    tabIndex={0}
	                    onClick={() => {
	                      if (!myVote && votingState?.isOpen && !isEliminatedPlayer) setSelectedVoteTarget(pid);
	                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
	                        if (!myVote && votingState?.isOpen && !isEliminatedPlayer) setSelectedVoteTarget(pid);
                      }
                    }}
                  >
                    <h4>{pname}</h4>
                    <div className="puzzle-voting-avatar">
                      {character ? (
                        <img
                          className="puzzle-voting-avatar-image"
                          src={`/img/${character}.png`}
                          alt={character}
                        />
                      ) : (
                        <span>🙂</span>
                      )}
                    </div>
                    {allVotesSubmitted ? (
                      <div className="puzzle-voting-count">{Number(perUserVoteCount[pid] || 0)} vote(s)</div>
                    ) : null}
                  </article>
                );
              })}
            </div>
            <p className="puzzle-voting-subtitle">
	              Votes received: {voteCount}/{activePlayers.length}
            </p>
            {myVote ? (
              <p className="puzzle-voting-subtitle">
                You voted for{" "}
                {myVote === "skip"
                  ? "Skip Vote"
	                  : (() => {
	                      const idx = activePlayers.findIndex(
	                        (p) => String(p?.userId || p?.id || "").trim() === myVote
	                      );
	                      return idx >= 0 ? resolvePlayerName(activePlayers[idx], idx + 1) : "a player";
	                    })()}
              </p>
            ) : null}
	            {finalOutcome ? (
	              <p className="puzzle-voting-subtitle">
	                {finalOutcomeMessage} Winner role: {finalOutcome.winnerRoleGroup || "-"} | Winners:{" "}
	                {(finalOutcome.winnerUserIds || []).length}
	              </p>
	            ) : result ? (
	              <p className="puzzle-voting-subtitle">
	                {result?.eliminatedUserId
	                  ? eliminationNotices[eliminationNotices.length - 1] || ""
	                  : voteResultNonEliminationMessage}
	              </p>
	            ) : null}

            <div className="puzzle-voting-actions">
              <button
                type="button"
	                className="puzzle-vote-btn skip-btn"
	                onClick={() => submitVote("skip")}
	                disabled={!votingState?.isOpen || Boolean(myVote) || isEliminatedPlayer}
              >
                Skip Vote
              </button>
              <button
                type="button"
	                className="puzzle-vote-btn confirm-btn"
	                onClick={() => submitVote()}
	                disabled={!votingState?.isOpen || Boolean(myVote) || !selectedVoteTarget || isEliminatedPlayer}
              >
                Confirm Vote
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isChatPopupOpen ? (
        <div className="puzzle-chat-overlay" onClick={closeChatPopup} role="presentation">
          <div
            className="puzzle-chat-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Quick chat popup"
          >
            <button type="button" className="puzzle-chat-close" onClick={closeChatPopup} aria-label="Close quick chat popup">
              X
            </button>

            <div className="puzzle-chat-players">
              {(players || []).slice(0, 4).map((p, idx) => {
                const name = resolvePlayerName(p, idx + 1);
                const character = String(p?.character || "").trim().toLowerCase();
                return (
                  <article className="puzzle-chat-player-card" key={`chat-player-${p?.userId || p?.id || idx}`}>
                    <h4>{name}</h4>
                    <div className="puzzle-chat-avatar">
                      {character ? (
                        <img
                          className="puzzle-chat-avatar-image"
                          src={`/img/${character}.png`}
                          alt={character}
                        />
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="puzzle-chat-messages" aria-label="Chat messages" ref={chatMessagesRef}>
              {chatMessages.length === 0 ? (
                <p>[{formatChatTime(Date.now())}] System: "No messages yet"</p>
              ) : (
                chatMessages.map((msg) => (
                  <p key={msg.id}>
                    [{formatChatTime(msg.createdAtMs)}] {msg.name}: "{msg.message}"
                  </p>
                ))
              )}
            </div>

            <div className="puzzle-chat-input-row">
              <input
                type="text"
                className="puzzle-chat-input"
                placeholder="Type here..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                disabled={sendingChat}
              />
              <button type="button" className="puzzle-chat-enter-btn" onClick={sendChatMessage} disabled={sendingChat}>
                Enter
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showKickedPopup ? (
        <div className="kicked-popup-overlay" role="presentation">
          <div
            className={`kicked-popup-card ${kickedConfig.themeClass}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="kicked-popup-title"
          >
            <div className="kicked-popup-avatar">
              {myCharacter ? (
                <img src={`/img/${myCharacter}.png`} alt={myCharacter} />
              ) : (
                <span className="kicked-popup-avatar-fallback" aria-hidden="true">!</span>
              )}
            </div>
            <h2 id="kicked-popup-title" className="kicked-popup-title">
              {kickedConfig.title}
            </h2>
            <p className="kicked-popup-task">{kickedConfig.message}</p>
            <button
              type="button"
              className="kicked-popup-next-btn"
              onClick={handleKickedDismiss}
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
